import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ArrowRight, Check } from 'lucide-react';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

// Checkout Form Component
const CheckoutForm = ({ formData, setFormData, onSubmit, isLoading, onCancel, variant = 'default' }) => (
  <form onSubmit={onSubmit} className="space-y-4 w-full max-w-sm mx-auto">
    <Input
      data-testid="insight-name-input"
      type="text"
      placeholder="Your name"
      value={formData.name}
      onChange={(e) => setFormData({...formData, name: e.target.value})}
      className={`h-14 px-5 text-base border-2 transition-all duration-300 focus:ring-0 ${
        variant === 'dark' 
          ? 'bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white/40' 
          : 'bg-white border-[#E5E2DF] text-[#2C2825] placeholder:text-[#A8A29E] focus:border-[#881337]'
      }`}
      required
    />
    <Input
      data-testid="insight-email-input"
      type="email"
      placeholder="Your email"
      value={formData.email}
      onChange={(e) => setFormData({...formData, email: e.target.value})}
      className={`h-14 px-5 text-base border-2 transition-all duration-300 focus:ring-0 ${
        variant === 'dark' 
          ? 'bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white/40' 
          : 'bg-white border-[#E5E2DF] text-[#2C2825] placeholder:text-[#A8A29E] focus:border-[#881337]'
      }`}
      required
    />
    <button
      data-testid="checkout-submit-btn"
      type="submit"
      disabled={isLoading}
      className={`w-full h-14 text-base font-medium tracking-wide transition-all duration-300 disabled:opacity-50 ${
        variant === 'dark'
          ? 'bg-white text-[#1C1917] hover:bg-white/90'
          : 'bg-[#881337] text-white hover:bg-[#6F0F2D]'
      }`}
    >
      {isLoading ? 'Processing...' : 'Get the Wedding Insight'}
    </button>
    <button 
      type="button"
      onClick={onCancel}
      className={`w-full text-sm transition-colors ${
        variant === 'dark' ? 'text-white/50 hover:text-white/80' : 'text-[#8A8580] hover:text-[#5C5652]'
      }`}
    >
      Cancel
    </button>
  </form>
);

const WeddingInsightPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [scrolled, setScrolled] = useState(false);

  // Force scroll to top on mount - prevents mid-page landing
  useEffect(() => {
    window.scrollTo(0, 0);
    // Disable scroll restoration for this page
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    return () => {
      // Restore default behavior when leaving
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handlePurchase = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Please enter your name and email');
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/wedding-insight/checkout`, {
        name: formData.name,
        email: formData.email
      });
      if (response.data.status === 'success' && response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error(response.data.message || 'Unable to start checkout');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToCheckout = () => {
    const purchaseSection = document.getElementById('purchase');
    if (purchaseSection) {
      // Get sticky header height (approximately 70px) and add buffer
      const headerOffset = 80;
      const elementPosition = purchaseSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 px-6 md:px-12 lg:px-20 py-5 transition-all duration-500 ${
        scrolled ? 'bg-[#FAF9F7]/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div 
            className="flex items-center gap-2.5 cursor-pointer" 
            onClick={() => navigate('/')}
            data-testid="nav-logo"
          >
            <Heart className="text-[#881337]" size={22} fill="#881337" />
            <span className="text-lg font-serif text-[#2C2825]">VowFlow</span>
          </div>
          <button
            onClick={scrollToCheckout}
            className="hidden sm:block text-sm font-medium text-[#2C2825] hover:text-[#881337] transition-colors"
          >
            Get the Insight
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#881337] mb-6">Wedding Insight</p>
          
          <h1 
            data-testid="insight-heading"
            className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#2C2825] leading-[1.1] mb-8"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            You Don't Need Another Checklist.{' '}
            <span className="text-[#881337]">You Need to Know If You're Missing Something.</span>
          </h1>
          
          <p className="text-lg md:text-xl text-[#5C5652] leading-relaxed mb-6 max-w-2xl mx-auto">
            Before deposits are locked in and decisions compound, get a personalized Wedding Insight that shows you what's aligned, what's risky, and what deserves your attention next.
          </p>

          <p className="text-base text-[#2C2825] italic mb-10">
            {"If you've already booked vendors but can't shake the feeling something's off — this is for you."}
          </p>

          <button
            data-testid="get-insight-btn"
            onClick={scrollToCheckout}
            className="px-8 py-4 bg-[#881337] text-white text-base font-medium hover:bg-[#6F0F2D] transition-all mb-4"
          >
            Get My Wedding Insight
          </button>
          
          <p className="text-sm text-[#5C5652]">
            $99 one-time. No subscription. Delivered within 24 hours.
          </p>
        </div>
      </section>

      {/* Real-World Foundation - Credibility Section */}
      <section className="py-14 md:py-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#881337] mb-6">Real-World Foundation</p>
          
          <div className="space-y-4 text-[#5C5652] leading-relaxed">
            <p className="text-lg">
              Created from real wedding planning experience — not generic AI advice.
            </p>
            <p className="text-base">
              {"VowFlow's logic is built on real-world timeline management, vendor coordination, and the same structured planning approach used behind the scenes of live weddings."}
            </p>
            <p className="text-base text-[#2C2825] font-medium mt-6">
              {"This is the second set of eyes most couples don't realize they need."}
            </p>
          </div>
        </div>
      </section>

      {/* Here's What Actually Happens */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-20 bg-white">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#881337] mb-4 text-center">Here's What Actually Happens</p>
          
          <div className="text-center space-y-6 text-lg text-[#5C5652] leading-relaxed">
            <p className="text-[#2C2825]">Most couples don't "mess up" their wedding.</p>
            <p>They make reasonable decisions — one at a time.</p>
            <p>
              But small assumptions stack.<br />
              Timelines tighten.<br />
              Budgets stretch.<br />
              Vendors get locked in.
            </p>
            <p>And no one steps back to ask:</p>
            <p className="text-[#2C2825] font-medium italic text-xl">Is this still aligned?</p>
            <p>That's where the Wedding Insight comes in.</p>
          </div>
        </div>
      </section>

      {/* What You'll Receive */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#881337] mb-4 text-center">{"What You'll Receive"}</p>
          
          <div className="space-y-4 mb-10">
            {[
              "A personalized snapshot of where you truly stand",
              "Clear signals about what's aligned vs. what's creating friction",
              "Blind spots most couples don't see until it's too late",
              "Guidance on what to prioritize now versus later",
              "A structured perspective built from real planning logic — not generic advice"
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-5 bg-white border border-[#E8E5E1]">
                <span className="w-1.5 h-1.5 bg-[#881337] rounded-full mt-2.5 flex-shrink-0" />
                <p className="text-[#2C2825] text-lg">{item}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-[#2C2825] font-medium">
            No fluff. No overwhelm. Just clarity.
          </p>
        </div>
      </section>

      {/* This Is For You If */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-20 bg-white">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#881337] mb-10 text-center">This Is For You If…</p>

          <div className="space-y-4">
            {[
              "You've already booked (or are about to book) key vendors",
              "You've made decisions that would be expensive to undo",
              "You want objective clarity before moving forward",
              "You don't want full-service planning — but you also don't want to wing it"
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-5 bg-[#FAF9F7]">
                <Check size={18} className="text-[#881337] mt-0.5 flex-shrink-0" />
                <p className="text-[#2C2825]">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-20 bg-white">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#881337] mb-4 text-center">How It Works</p>

          <div className="grid md:grid-cols-4 gap-8 mt-12">
            {[
              { step: '1', title: 'Purchase the Wedding Insight' },
              { step: '2', title: 'Complete a short intake (10–15 minutes)' },
              { step: '3', title: 'VowFlow analyzes your planning signals' },
              { step: '4', title: 'Receive your structured Insight within 24 hours' }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-10 h-10 bg-[#881337] text-white font-serif text-lg flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-[#2C2825] font-medium">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What This Is Not */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#881337] mb-10 text-center">What This Is Not</p>

          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            {[
              'Not full-service wedding planning',
              'Not a vendor referral list',
              'Not a generic AI report',
              'Not a sales call in disguise'
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-4 border border-[#E8E5E1]">
                <span className="text-[#A8A29E]">—</span>
                <p className="text-[#5C5652]">{item}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-[#2C2825] font-medium">
            It's a focused, one-time clarity checkpoint.
          </p>
        </div>
      </section>

      {/* Why $99 - Pricing */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#881337] mb-4 text-center">Why $99?</p>
          
          <div className="text-center space-y-6 text-lg text-[#5C5652] leading-relaxed mb-12">
            <p className="text-[#2C2825]">Because most wedding stress doesn't come from big mistakes.</p>
            <p>It comes from small ones that compound.</p>
            <p>
              $99 is small compared to:<br />
              • A rushed timeline<br />
              • A non-refundable vendor<br />
              • A budget that quietly balloons<br />
              • A day that feels tight instead of effortless
            </p>
            <p className="text-[#2C2825]">This is a checkpoint — before those things happen.</p>
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section id="purchase" className="py-20 md:py-28 px-6 md:px-12 lg:px-20 bg-[#2C2825]">
        <div className="max-w-md mx-auto text-center">
          <div className="mb-6">
            <span className="font-serif text-6xl md:text-7xl text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>$99</span>
            <p className="text-white/50 text-sm mt-2 uppercase tracking-wide">One Time</p>
          </div>

          <div className="space-y-2 text-white/60 text-sm mb-6">
            <p>No subscription</p>
            <p>No ongoing commitment</p>
            <p>No pressure to upgrade</p>
          </div>
          
          <p className="text-white/80 text-sm mb-10">
            If you don't feel this Insight gave you real clarity, email within 7 days and we'll refund you.
          </p>

          {!showCheckout ? (
            <div className="space-y-4">
              <button
                data-testid="get-insight-btn-bottom"
                onClick={() => setShowCheckout(true)}
                className="w-full sm:w-auto px-10 py-4 bg-white text-[#2C2825] text-base font-medium hover:bg-white/90 transition-all flex items-center justify-center gap-2 mx-auto"
              >
                Get My $99 Wedding Insight
                <ArrowRight size={18} />
              </button>
              <p className="text-sm text-white/50">
                {"You'll receive your intake form immediately after purchase."}
              </p>
            </div>
          ) : (
            <CheckoutForm 
              formData={formData}
              setFormData={setFormData}
              onSubmit={handlePurchase}
              isLoading={isLoading}
              onCancel={() => setShowCheckout(false)}
              variant="dark"
            />
          )}
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl mx-auto text-center">
          <h2 
            className="font-serif text-3xl sm:text-4xl text-[#2C2825] mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Clarity Now. Confidence Later.
          </h2>
          <p className="text-lg text-[#5C5652] mb-8">
            One focused Insight. No subscription. No pressure. Just perspective.
          </p>
          <button
            onClick={scrollToCheckout}
            className="px-8 py-4 bg-[#881337] text-white text-base font-medium hover:bg-[#6F0F2D] transition-all"
          >
            Get My $99 Wedding Insight
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 md:px-12 lg:px-20 border-t border-[#E8E5E1]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Heart className="text-[#881337]" size={18} fill="#881337" />
            <span className="font-serif text-[#2C2825]">VowFlow</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-[#8A8580]">
            <button onClick={() => navigate('/')} className="hover:text-[#2C2825] transition-colors">Home</button>
            <button onClick={() => navigate('/login')} className="hover:text-[#2C2825] transition-colors">Login</button>
            <button onClick={() => navigate('/signup')} className="hover:text-[#2C2825] transition-colors">Sign Up</button>
          </div>
          <p className="text-xs text-[#A8A29E]">© {new Date().getFullYear()} VowFlow</p>
        </div>
      </footer>
    </div>
  );
};

export default WeddingInsightPage;
